import React, { useCallback } from 'react'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useRequests } from '../requests/useRequests'
import { RefreshActions } from '../state/refresh'
import {
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Autocomplete
} from '@mui/material'
import { Group } from '../types/group'
import { State } from '../types/state'
import { Role } from '../types/user'
import { ErrorAlert } from '../formik/ErrorAlert'

interface NewOrEditGroupDialogProps {
  group: Group | null
  handleClose: (b: boolean) => void
  caregiverRoles: Role[]
}

const NewOrEditGroupDialog = ({
  group,
  handleClose,
  caregiverRoles
}: NewOrEditGroupDialogProps): JSX.Element => {
  const { users } = useSelector((state: State) => state.user)
  const dispatch = useDispatch()
  const requestMaker = useRequests()

  const initGroup = {
    id: group?.id,
    name: group?.name,
    caregiverId: group?.caregiverId,
    caregiver: group?.caregiver,
    students: group?.students
  }

  const validate = (group: Partial<Group>): Partial<Group> => {
    const errors: Partial<Group> = {}

    if (!group?.name || group.name === '') {
      errors.name = 'Name cannot be empty.'
    }

    if (!group.caregiverId) {
      errors.caregiver = 'Teacher/Tutor/Form Tutor cannot be empty.' as any
    }

    if (group.students == null || group.students.length === 0) {
      errors.students = 'Choose at least one student.' as any
    }

    return errors
  }

  const onSubmit = useCallback(
    async (group: Partial<Group>): Promise<void> => {
      handleClose(true)
      try {
        const isEdited = group.id !== undefined

        const userIdsGroup = {
          ...group,
          userIds: group.students?.map(s => s.user.id) ?? []
        }

        if (isEdited) {
          await requestMaker.updateGroup(userIdsGroup)
        } else {
          await requestMaker.createGroup(userIdsGroup)
        }
        dispatch(RefreshActions.refreshUserState())
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Succesfully update group'
          })
        )
      } catch (e) {
        dispatch(
          RefreshActions.setToastMessage({
            message:
              'Error - fill all neccessary fields correctly and try again'
          })
        )
      }
    },
    [requestMaker, dispatch]
  )

  const formik = useFormik({
    initialValues: initGroup,
    onSubmit,
    enableReinitialize: true,
    validate
  })

  return (
    <div>
      <Dialog open onClose={handleClose}>
        <form>
          <DialogTitle>Group</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              error={!!formik.errors.name && formik.touched.name}
              helperText={
                !!formik.errors.name && formik.touched.name
                  ? formik.errors.name
                  : null
              }
              margin='dense'
              id='name'
              label='Name'
              type='name'
              fullWidth
              variant='standard'
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            <FormControl fullWidth style={{ marginTop: '1vw' }}>
              <InputLabel id='caregiver'>Teacher/Tutor/Form Tutor</InputLabel>
              <Select
                labelId='caregiver'
                error={!!formik.errors.caregiver && formik.touched.caregiver}
                id='caregiver'
                value={formik.values.caregiverId}
                label='Teacher/Tutor/Form Tutor'
                onChange={e => {
                  void formik.setFieldValue('caregiverId', e.target.value)
                  void formik.setFieldValue(
                    'caregiver',
                    users.find(u => u.id === e.target.value)
                  )
                }}
              >
                {users
                  .filter(u => caregiverRoles.some(c => c === u.role))
                  .map((u, index) => (
                    <MenuItem key={index} value={u.id}>
                      {`${u.firstName} ${u.lastName}`}
                    </MenuItem>
                  ))}
              </Select>
              <ErrorAlert formik={formik} name='caregiver' />
            </FormControl>
            <FormControl fullWidth style={{ marginTop: '1vw' }}>
              <Autocomplete
                sx={{ m: 1, width: 500 }}
                multiple
                value={formik.values.students?.map(s => s.user) ?? []}
                options={users.filter(u => u.role === Role.STUDENT)}
                getOptionLabel={option =>
                  option?.firstName && option?.lastName
                    ? `${option.firstName} ${option.lastName}`
                    : option?.username
                }
                isOptionEqualToValue={(a, b) => a.id === b.id}
                disableCloseOnSelect
                onChange={(_, students) =>
                  void formik.setFieldValue(
                    'students',
                    students.map(s => ({ user: s }))
                  )
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    error={!!formik.errors.students && formik.touched.students}
                    helperText={
                      !!formik.errors.students && formik.touched.students
                        ? formik.errors.students
                        : null
                    }
                    variant='outlined'
                    label='Students'
                    placeholder='Students'
                  />
                )}
              />
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose as any}>Cancel</Button>
            <Button onClick={formik.handleSubmit as any}>Add</Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}

export default NewOrEditGroupDialog
